/*
 * ******************************************************************
 * Copyright (c) 2021-2024 Broadcom. All Rights Reserved.
 * Broadcom Confidential. The term "Broadcom" refers to Broadcom Inc.
 * and/or its subsidiaries.
 * ******************************************************************
 */

import { Injectable } from "@angular/core";
import { OAuth2Client } from "@vmw/csp-oauth2";
import { OAuth2TokenSubject } from "@vmw/ngx-csp-oauth2";
import { ConsoleType } from "@shared/models/global.model";
import { getUser, LoggerService, User } from "@app/services";
import { OPERATOR_ROLES, OperatorRoleEnum, UiPrivilegeEnum, VLCR_CONSOLE_ROLES, VLCR_ROLES, VlrRoleEnum, VMCFS_CONSOLE_ROLES, VMCFS_ROLES, VmcfsRoleEnum } from "./roles.model";
import { SetUtil } from "@shared/utils/set-util";

// @formatter:off
@Injectable({
    providedIn: "root"
})
// @formatter:on
export class RbacService {

    private rolePrivilegeMap: any = {};
    private currentUser: User = null;
    private truncatedRoles: string[] = [];
    private currentPrivileges: UiPrivilegeEnum[] = [];

    constructor(private oAuth2Client: OAuth2Client,
                private oAuth2TokenSubject: OAuth2TokenSubject,
                private logger: LoggerService) {
        this.initRolePrivilegeMap();
        this.oAuth2TokenSubject.subscribe(token => {
            if (token) {
                this.currentUser = getUser(this.oAuth2Client);
                this.truncatedRoles = this.getTruncatedRoles();
                this.currentPrivileges = this.getPrivileges();

                // Dev debug logging
                this.logger.debug("roles:", this.truncatedRoles);
                this.logger.debug("privileges:", this.currentPrivileges);
            }
        });
    }

    public hasOperatorRole(): boolean {
        return Boolean(SetUtil.intersection(OPERATOR_ROLES, this.truncatedRoles).length > 0);
    }

    public hasVlrRole(): boolean {
        return Boolean(SetUtil.intersection(VLCR_ROLES, this.truncatedRoles).length > 0);
    }

    public hasVmcfsRole(): boolean {
        return Boolean(SetUtil.intersection(VMCFS_ROLES, this.truncatedRoles).length > 0);
    }

    public hasAnyRole(consoleType: ConsoleType): boolean {
        switch (consoleType) {
            case ConsoleType.OPERATOR:
                return this.hasOperatorRole();
            case ConsoleType.VLR:
                return this.hasVlrRole();
            case ConsoleType.VMCFS:
                return this.hasVmcfsRole();
            default:
                console.log("Unknown console type");
                return false;
        }
    }

    public getConsoleAdminRole(consoleType: ConsoleType): string {
        switch (consoleType) {
            case ConsoleType.VLR:
                return VlrRoleEnum.CONSOLE_ADMIN;
            case ConsoleType.VMCFS:
                return VmcfsRoleEnum.CONSOLE_ADMIN;
            default:
                return null;
        }
    }

    public hasConsoleAdminRole(consoleType: ConsoleType): boolean {
        const role = this.getConsoleAdminRole(consoleType);
        return this.hasRole(role);
    }

    public hasRoles(roles: OperatorRoleEnum[]): boolean {
        return Boolean(SetUtil.intersection(roles, this.truncatedRoles).length > 0);
    }

    public hasRole(role: string): boolean {
        return Boolean(this.truncatedRoles.includes(role));
    }

    public hasPrivilege(privilege: UiPrivilegeEnum): boolean {
        return this.currentPrivileges.includes(privilege);
    }

    public hasNoPrivileges(): boolean {
        return this.currentPrivileges.length === 0;
    }

    public hasOneOfPrivileges(privileges: UiPrivilegeEnum[]): boolean {
        if (this.currentPrivileges.length == 0) {
            return false;
        }
        for (let i = 0; i < privileges.length; i++) {
            if (this.currentPrivileges.includes(privileges[i])) {
                return true;
            }
        }
        return false;
    }

    public hasAllPrivileges(privileges: UiPrivilegeEnum[]): boolean {
        if (this.currentPrivileges.length == 0) {
            return false;
        }
        for (let i = 0; i < privileges.length; i++) {
            if (!this.currentPrivileges.includes(privileges[i])) {
                return false;
            }
        }
        return true;
    }

    public hasConsoleRole(consoleType: ConsoleType): boolean {
        switch (consoleType) {
            case ConsoleType.VLR:
                return this.hasVlrConsoleRole();
            case ConsoleType.VMCFS:
                return this.hasVmcfsConsoleRole();
            default:
                return false;
        }
    }

    public hasVlrConsoleRole(): boolean {
        return Boolean(SetUtil.intersection(VLCR_CONSOLE_ROLES, this.truncatedRoles).length > 0);
    }

    public hasVmcfsConsoleRole(): boolean {
        return Boolean(SetUtil.intersection(VMCFS_CONSOLE_ROLES, this.truncatedRoles).length > 0);
    }

    private initRolePrivilegeMap() {
        // VLCR: "Orchestrator Admin"
        this.rolePrivilegeMap[VlrRoleEnum.VLCR_ADMIN] = [
            UiPrivilegeEnum.VIEW_DEPLOYMENTS
        ];
        // VLCR: "DR Admin"
        this.rolePrivilegeMap[VlrRoleEnum.VLCR_DR_ADMIN] = [
            UiPrivilegeEnum.VIEW_DEPLOYMENTS
        ];
        // VLCR: "DR SDDC Admin"
        this.rolePrivilegeMap[VlrRoleEnum.VLCR_DR_SDDC_ADMIN] = [
            UiPrivilegeEnum.VIEW_DEPLOYMENTS
        ];
        // VLCR: "DR Tester"
        this.rolePrivilegeMap[VlrRoleEnum.VLCR_DR_TESTER] = [
            UiPrivilegeEnum.VIEW_DEPLOYMENTS
        ];
        // VLCR: "Data Protection Auditor"
        this.rolePrivilegeMap[VlrRoleEnum.VLCR_AUDITOR] = [
            UiPrivilegeEnum.VIEW_DEPLOYMENTS
        ];
        // VLCR: "Protection Admin"
        this.rolePrivilegeMap[VlrRoleEnum.VLCR_BACKUP_ADMIN] = [
            UiPrivilegeEnum.VIEW_DEPLOYMENTS
        ];

        // VLCR Console: "Global Console Admin"
        this.rolePrivilegeMap[VlrRoleEnum.CONSOLE_ADMIN] = [
            UiPrivilegeEnum.VIEW_DEPLOYMENTS,
            UiPrivilegeEnum.CREATE_DEPLOYMENT,
            UiPrivilegeEnum.DELETE_DEPLOYMENT,
            UiPrivilegeEnum.VIEW_SUBSCRIPTIONS,
            UiPrivilegeEnum.VIEW_ACTIVITY_LOG,
            UiPrivilegeEnum.VIEW_SETTINGS
        ];
        // VLCR Console: "Deployment Admin (Activation)"
        this.rolePrivilegeMap[VlrRoleEnum.CONSOLE_ACTIVATE_DEPLOYMENT] = [
            UiPrivilegeEnum.VIEW_DEPLOYMENTS,
            UiPrivilegeEnum.CREATE_DEPLOYMENT,
            UiPrivilegeEnum.VIEW_ACTIVITY_LOG,
            UiPrivilegeEnum.VIEW_SETTINGS
        ];
        // VLCR Console: "Deployment Admin (Deactivation)"
        this.rolePrivilegeMap[VlrRoleEnum.CONSOLE_DEACTIVATE_DEPLOYMENT] = [
            UiPrivilegeEnum.VIEW_DEPLOYMENTS,
            UiPrivilegeEnum.DELETE_DEPLOYMENT,
            UiPrivilegeEnum.VIEW_ACTIVITY_LOG,
            UiPrivilegeEnum.VIEW_SETTINGS
        ];
        // VLCR Console: "Subscription Admin"
        this.rolePrivilegeMap[VlrRoleEnum.CONSOLE_SUBSCRIPTION_ADMIN] = [
            UiPrivilegeEnum.VIEW_SUBSCRIPTIONS,
            UiPrivilegeEnum.VIEW_ACTIVITY_LOG
        ];
        // VLCR Console: "Subscription Auditor"
        this.rolePrivilegeMap[VlrRoleEnum.CONSOLE_SUBSCRIPTION_AUDITOR] = [
            UiPrivilegeEnum.VIEW_SUBSCRIPTIONS,
            UiPrivilegeEnum.VIEW_ACTIVITY_LOG
        ];

        // Operator Console: "Read-Only Operator"
        this.rolePrivilegeMap[OperatorRoleEnum.OPERATOR_READ_ONLY] = [
            UiPrivilegeEnum.OP_READ_ENTITIES
        ];
        // Operator Console: "Read-Write Operator"
        this.rolePrivilegeMap[OperatorRoleEnum.OPERATOR_READ_WRITE] = [
            UiPrivilegeEnum.OP_READ_ENTITIES,
            UiPrivilegeEnum.OP_WRITE_ENTITIES
        ];
        // Operator Console: "Engineering Read Only"
        this.rolePrivilegeMap[OperatorRoleEnum.ENG_READ_ONLY] = [
            UiPrivilegeEnum.OP_READ_ENTITIES
        ];
        // Operator Console: "Engineering Read-Write"
        this.rolePrivilegeMap[OperatorRoleEnum.ENG_READ_WRITE] = [
            UiPrivilegeEnum.OP_READ_ENTITIES,
            UiPrivilegeEnum.OP_WRITE_ENTITIES
        ];
        // Operator Console: "Console Support Read Only"
        this.rolePrivilegeMap[OperatorRoleEnum.CONSOLE_SUPPORT_READ_ONLY] = [
            UiPrivilegeEnum.OP_READ_ENTITIES
        ];
        // Operator Console: "Console Support Read-Write"
        this.rolePrivilegeMap[OperatorRoleEnum.CONSOLE_SUPPORT_READ_WRITE] = [
            UiPrivilegeEnum.OP_READ_ENTITIES,
            UiPrivilegeEnum.OP_WRITE_ENTITIES
        ];
        // Operator Console: "DevOps Read Only"
        this.rolePrivilegeMap[OperatorRoleEnum.DEVOPS_READ_ONLY] = [
            UiPrivilegeEnum.OP_READ_ENTITIES
        ];
        // Operator Console: "DevOps Read-Write"
        this.rolePrivilegeMap[OperatorRoleEnum.DEVOPS_READ_WRITE] = [
            UiPrivilegeEnum.OP_READ_ENTITIES,
            UiPrivilegeEnum.OP_WRITE_ENTITIES
        ];
        // Operator Console: "DPX Inventory Read Only"
        this.rolePrivilegeMap[OperatorRoleEnum.VLR_INVENTORY_READ_ONLY] = [
            UiPrivilegeEnum.OP_READ_ENTITIES
        ];
        // Operator Console: "DPX Inventory Read-Write"
        this.rolePrivilegeMap[OperatorRoleEnum.VLR_INVENTORY_READ_WRITE] = [
            UiPrivilegeEnum.OP_READ_ENTITIES,
            UiPrivilegeEnum.OP_WRITE_ENTITIES
        ];

        // VMCFS: "Storage Admin"
        this.rolePrivilegeMap[VmcfsRoleEnum.STORAGE_ADMIN] = [
            UiPrivilegeEnum.VIEW_DEPLOYMENTS
        ];
        // VMCFS: "Storage Auditor"
        this.rolePrivilegeMap[VmcfsRoleEnum.STORAGE_AUDITOR] = [
            UiPrivilegeEnum.VIEW_DEPLOYMENTS
        ];
        // VMCFS: "Filesystem Admin"
        this.rolePrivilegeMap[VmcfsRoleEnum.FILESYSTEM_ADMIN] = [
            UiPrivilegeEnum.VIEW_DEPLOYMENTS
        ];
        // VMCFS: "Filesystem Delete Restricted"
        this.rolePrivilegeMap[VmcfsRoleEnum.FILESYSTEM_ADMIN_RESTRICTED] = [
            UiPrivilegeEnum.VIEW_DEPLOYMENTS
        ];
        // VMCFS: "Console Storage Admin"
        this.rolePrivilegeMap[VmcfsRoleEnum.CONSOLE_STORAGE_ADMIN] = [
            UiPrivilegeEnum.VIEW_DEPLOYMENTS
        ];
        // VMCFS: "Global Console Admin"
        this.rolePrivilegeMap[VmcfsRoleEnum.CONSOLE_ADMIN] = [
            UiPrivilegeEnum.VIEW_DEPLOYMENTS,
            UiPrivilegeEnum.CREATE_DEPLOYMENT,
            UiPrivilegeEnum.DELETE_DEPLOYMENT,
            UiPrivilegeEnum.VIEW_SUBSCRIPTIONS,
            UiPrivilegeEnum.VIEW_ACTIVITY_LOG,
            UiPrivilegeEnum.VIEW_SETTINGS
        ];
        // VMCFS: "Deployment Admin (Activation)"
        this.rolePrivilegeMap[VmcfsRoleEnum.CONSOLE_ACTIVATE_DEPLOYMENT] = [
            UiPrivilegeEnum.VIEW_DEPLOYMENTS,
            UiPrivilegeEnum.CREATE_DEPLOYMENT,
            UiPrivilegeEnum.VIEW_ACTIVITY_LOG,
            UiPrivilegeEnum.VIEW_SETTINGS
        ];
        // VMCFS: "Deployment Admin (Deactivation)"
        this.rolePrivilegeMap[VmcfsRoleEnum.CONSOLE_DEACTIVATE_DEPLOYMENT] = [
            UiPrivilegeEnum.VIEW_DEPLOYMENTS,
            UiPrivilegeEnum.DELETE_DEPLOYMENT,
            UiPrivilegeEnum.VIEW_ACTIVITY_LOG,
            UiPrivilegeEnum.VIEW_SETTINGS
        ];
        // VMCFS: "Subscription Admin"
        this.rolePrivilegeMap[VmcfsRoleEnum.CONSOLE_SUBSCRIPTION_ADMIN] = [
            UiPrivilegeEnum.VIEW_SUBSCRIPTIONS,
            UiPrivilegeEnum.VIEW_ACTIVITY_LOG
        ];
    }

    private getTruncatedRoles(): string[] {
        const roles: string[] = (this.currentUser && this.currentUser.roles) ? this.currentUser.roles : [];
        const truncatedRoles: string[] = roles.map((val: string) => {
            const tokenized: string[] = val.split("/");
            return tokenized[tokenized.length - 1];
        });
        return truncatedRoles;
    }

    private getPrivileges(): UiPrivilegeEnum[] {
        const userPrivilegeSet: Set<UiPrivilegeEnum> =
            this.truncatedRoles.reduce((acc, role) => {
                const privileges: UiPrivilegeEnum[] = this.rolePrivilegeMap[role];
                if (privileges?.length > 0) {
                    privileges.forEach(privilege => {
                        acc.add(privilege);
                    });
                }
                return acc;
            }, new Set<UiPrivilegeEnum>());
        return Array.from(userPrivilegeSet);
    }
}
